<template>
  <div class="dashboard-page">
    <div class="page-card">
      <div class="workbench-header">
        <h2 class="page-title">工作台</h2>
        <el-button
          v-if="canQuickSendSystemNotice"
          type="primary"
          icon="el-icon-bell"
          @click="goMessageSend"
        >
          快捷发送系统通知
        </el-button>
      </div>

      <div class="overview-panel" v-loading="overviewLoading">
        <div class="overview-header">
          <div>
            <div class="overview-title">平台统计</div>
            <div class="overview-subtitle">艺人、商家、招募通告与订单数据概览</div>
          </div>
          <div class="overview-total">
            <span class="overview-total__label">总量</span>
            <span class="overview-total__value">{{ formatStatValue(totalOverviewCount) }}</span>
          </div>
        </div>

        <div class="stats-grid">
          <div
            v-for="item in overviewStats"
            :key="item.key"
            class="stats-card"
            :class="`stats-card--${item.key}`"
          >
            <div class="stats-card__label">{{ item.label }}</div>
            <div class="stats-card__value">{{ formatStatValue(item.value) }}</div>
            <div class="stats-card__progress">
              <div
                class="stats-card__progress-fill"
                :class="`stats-card__progress-fill--${item.key}`"
                :style="{ width: `${item.ratio}%` }"
              ></div>
            </div>
            <div class="stats-card__hint">{{ item.hint }}</div>
          </div>
        </div>

        <div class="chart-panel">
          <div class="chart-panel__header">
            <div class="chart-panel__title">数量统计图</div>
            <div class="chart-panel__subtitle">四类核心数据对比</div>
          </div>
          <div class="chart-bars">
            <div v-for="item in overviewChartBars" :key="item.key" class="chart-bar-item">
              <div class="chart-bar-item__value">{{ formatStatValue(item.value) }}</div>
              <div class="chart-bar-track">
                <div
                  class="chart-bar"
                  :class="`chart-bar--${item.key}`"
                  :style="{ height: `${item.height}%` }"
                ></div>
              </div>
              <div class="chart-bar-item__label">{{ item.label }}</div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="canQuickSendSystemNotice" class="quick-send-banner" @click="goMessageSend">
        <div class="quick-send-banner__icon">
          <i class="el-icon-message-solid"></i>
        </div>
        <div class="quick-send-banner__body">
          <div class="quick-send-banner__title">系统通知快捷发送</div>
          <div class="quick-send-banner__desc">面向商家、艺人快速发送站内系统通知，发送结果可在发送任务记录中查看。</div>
        </div>
        <el-button type="primary" plain>立即前往</el-button>
      </div>
    </div>

    <div class="page-card audit-workbench">
      <div class="workbench-header">
        <h2 class="page-title">审核工作台</h2>
        <el-button type="text" @click="goAuditCenter">进入审核中心</el-button>
      </div>

      <el-tabs v-model="activeAuditTab" @tab-click="handleTabChange">
        <el-tab-pane label="商家认证" name="merchant-auth" />
        <el-tab-pane label="艺人实名" name="artist-realname" />
        <el-tab-pane label="艺人资料" name="artist-profile" />
        <el-tab-pane label="艺人IP档案" name="artist-star-profile" />
        <el-tab-pane label="才艺视频审核" name="artist-talent-video" />
        <el-tab-pane label="通告审核" name="casting-notice" />
      </el-tabs>

      <el-table :data="currentPagedList" border v-loading="loading">
        <el-table-column prop="submitterName" label="提交主体/标题" min-width="220" />
        <el-table-column prop="submitTime" label="提交时间" width="180" />
        <el-table-column prop="status" label="状态" width="120">
          <template slot-scope="{ row }">
            <el-tag :type="statusTagType(row.status)">
              {{ statusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="140">
          <template slot-scope="{ row }">
            <el-button type="text" @click="goDashboardDetail(row)">查看详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrap">
        <el-pagination
          background
          layout="total, prev, pager, next"
          :total="currentTabList.length"
          :page-size="dashboardPageSize"
          :current-page="tabPageMap[activeAuditTab]"
          @current-change="handleDashboardPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { hasPermission } from '../../utils/auth'
import { getDashboardOverview } from '../../api/platformManage'
import {
  getArtistProfileAudits,
  getArtistStarProfileAudits,
  getArtistTalentVideoAudits,
  getArtistRealnameAudits,
  getCastingNoticeAudits,
  getMerchantAuthAudits
} from '../../api/audit'
import { navigateWithFallback } from '../../utils/admin-navigation'

const DEFAULT_AUDIT_TAB = 'merchant-auth'
const DEFAULT_PAGE = 1
const DASHBOARD_PAGE_SIZE = 5

export default {
  name: 'DashboardPage',
  data() {
    return {
      activeAuditTab: DEFAULT_AUDIT_TAB,
      dashboardPageSize: DASHBOARD_PAGE_SIZE,
      loading: false,
      overviewLoading: false,
      overview: {
        artistCount: 0,
        merchantCount: 0,
        recruitingNoticeCount: 0,
        orderCount: 0
      },
      auditTabData: {
        'merchant-auth': [],
        'artist-realname': [],
        'artist-profile': [],
        'artist-star-profile': [],
        'artist-talent-video': [],
        'casting-notice': []
      },
      tabPageMap: {
        'merchant-auth': DEFAULT_PAGE,
        'artist-realname': DEFAULT_PAGE,
        'artist-profile': DEFAULT_PAGE,
        'artist-star-profile': DEFAULT_PAGE,
        'artist-talent-video': DEFAULT_PAGE,
        'casting-notice': DEFAULT_PAGE
      }
    }
  },
  computed: {
    adminName() {
      return this.$store.getters.adminName
    },
    canQuickSendSystemNotice() {
      return hasPermission('ops_message_task:create')
    },
    overviewStats() {
      const max = this.maxOverviewValue
      return [
        {
          key: 'artist',
          label: '艺人数量',
          value: this.overview.artistCount,
          hint: '平台注册艺人总量',
          ratio: this.calcOverviewRatio(this.overview.artistCount, max)
        },
        {
          key: 'merchant',
          label: '商家数量',
          value: this.overview.merchantCount,
          hint: '平台注册商家总量',
          ratio: this.calcOverviewRatio(this.overview.merchantCount, max)
        },
        {
          key: 'notice',
          label: '招募通告数',
          value: this.overview.recruitingNoticeCount,
          hint: '当前处于招募中的通告',
          ratio: this.calcOverviewRatio(this.overview.recruitingNoticeCount, max)
        },
        {
          key: 'order',
          label: '订单数量',
          value: this.overview.orderCount,
          hint: '平台累计订单总量',
          ratio: this.calcOverviewRatio(this.overview.orderCount, max)
        }
      ]
    },
    totalOverviewCount() {
      return (
        Number(this.overview.artistCount || 0) +
        Number(this.overview.merchantCount || 0) +
        Number(this.overview.recruitingNoticeCount || 0) +
        Number(this.overview.orderCount || 0)
      )
    },
    maxOverviewValue() {
      return Math.max(
        Number(this.overview.artistCount || 0),
        Number(this.overview.merchantCount || 0),
        Number(this.overview.recruitingNoticeCount || 0),
        Number(this.overview.orderCount || 0),
        0
      )
    },
    overviewChartBars() {
      const max = Math.max(...this.overviewStats.map(item => Number(item.value) || 0), 0)
      return this.overviewStats.map(item => ({
        ...item,
        height: max > 0 ? Math.max(16, Math.round(((Number(item.value) || 0) / max) * 100)) : 16
      }))
    },
    currentTabList() {
      return this.auditTabData[this.activeAuditTab] || []
    },
    currentPagedList() {
      const page = this.tabPageMap[this.activeAuditTab] || DEFAULT_PAGE
      const start = (page - 1) * this.dashboardPageSize
      return this.currentTabList.slice(start, start + this.dashboardPageSize)
    }
  },
  created() {
    this.restoreDashboardQuery()
    this.fetchDashboardOverview()
    this.fetchCurrentAuditData()
  },
  watch: {
    '$route.query': {
      deep: true,
      handler(newQuery, oldQuery) {
        const previousType = oldQuery && oldQuery.auditType
        this.restoreDashboardQuery()
        if (this.activeAuditTab !== (previousType || DEFAULT_AUDIT_TAB)) {
          this.fetchCurrentAuditData()
        }
      }
    }
  },
  methods: {
    formatStatValue(value) {
      return Number(value || 0).toLocaleString('zh-CN')
    },
    calcOverviewRatio(value, max) {
      const safeMax = Number(max || 0)
      if (!safeMax) {
        return 8
      }
      return Math.max(8, Math.round((Number(value || 0) / safeMax) * 100))
    },
    restoreDashboardQuery() {
      const query = this.$route.query || {}
      this.activeAuditTab = query.auditType || DEFAULT_AUDIT_TAB
      const page = query.auditPage ? Number(query.auditPage) : DEFAULT_PAGE
      this.$set(this.tabPageMap, this.activeAuditTab, page)
    },
    async fetchDashboardOverview() {
      this.overviewLoading = true
      try {
        const data = await getDashboardOverview()
        this.overview = {
          artistCount: Number(data && data.artistCount) || 0,
          merchantCount: Number(data && data.merchantCount) || 0,
          recruitingNoticeCount: Number(data && data.recruitingNoticeCount) || 0,
          orderCount: Number(data && data.orderCount) || 0
        }
      } catch (error) {
        this.$message.error(error.message || '加载工作台统计数据失败')
      } finally {
        this.overviewLoading = false
      }
    },
    syncDashboardQuery() {
      const nextQuery = {
        ...this.$route.query,
        auditType: this.activeAuditTab,
        auditPage: String(this.tabPageMap[this.activeAuditTab] || DEFAULT_PAGE)
      }
      if (
        this.$route.query.auditType === nextQuery.auditType &&
        this.$route.query.auditPage === nextQuery.auditPage
      ) {
        return false
      }
      this.$router.replace({ path: '/dashboard', query: nextQuery })
      return true
    },
    getAuditFetcher(type) {
      if (type === 'merchant-auth') return getMerchantAuthAudits
      if (type === 'artist-realname') return getArtistRealnameAudits
      if (type === 'artist-profile') return getArtistProfileAudits
      if (type === 'artist-star-profile') return getArtistStarProfileAudits
      if (type === 'artist-talent-video') return getArtistTalentVideoAudits
      return getCastingNoticeAudits
    },
    statusText(status) {
      if (this.activeAuditTab === 'casting-notice') {
        if (status === 2) return '待审核'
        if (status === 3) return '招募中'
        if (status === 4) return '已驳回'
        if (status === 5) return '招募中'
        if (status === 6) return '已下架'
        if (status === 9) return '已封禁'
      } else {
        if (status === 1) return '待审核'
        if (status === 2) return '已通过'
        if (status === 3) return '已驳回'
        if (status === 4) return '待补充材料'
      }
      return status == null ? '-' : String(status)
    },
    statusTagType(status) {
      if (this.activeAuditTab === 'casting-notice') {
        if (status === 2) return 'warning'
        if (status === 3 || status === 5) return 'success'
        if (status === 4 || status === 9) return 'danger'
        if (status === 6) return 'info'
      } else {
        if (status === 1) return 'warning'
        if (status === 2) return 'success'
        if (status === 3) return 'danger'
        if (status === 4) return 'info'
      }
      return 'info'
    },
    async fetchAuditData(type) {
      this.loading = true
      try {
        const list = await this.getAuditFetcher(type)({ status: 1 })
        this.$set(this.auditTabData, type, list || [])
        if (type === this.activeAuditTab) {
          this.correctCurrentPage()
        }
      } catch (error) {
        this.$message.error(error.message || '加载工作台审核数据失败')
      } finally {
        this.loading = false
      }
    },
    async fetchCurrentAuditData() {
      await this.fetchAuditData(this.activeAuditTab)
    },
    correctCurrentPage() {
      const list = this.currentTabList
      const maxPage = Math.max(1, Math.ceil(list.length / this.dashboardPageSize))
      if ((this.tabPageMap[this.activeAuditTab] || DEFAULT_PAGE) > maxPage) {
        this.$set(this.tabPageMap, this.activeAuditTab, maxPage)
        this.syncDashboardQuery()
      }
    },
    handleTabChange() {
      if (!this.tabPageMap[this.activeAuditTab]) {
        this.$set(this.tabPageMap, this.activeAuditTab, DEFAULT_PAGE)
      }
      this.correctCurrentPage()
      const changed = this.syncDashboardQuery()
      if (!changed) {
        this.fetchCurrentAuditData()
      }
    },
    handleDashboardPageChange(page) {
      this.$set(this.tabPageMap, this.activeAuditTab, page)
      this.syncDashboardQuery()
    },
    goAuditCenter() {
      navigateWithFallback(this, {
        path: '/audits',
        query: {
          type: this.activeAuditTab,
          status: '1',
          page: '1'
        }
      })
    },
    goMessageSend() {
      navigateWithFallback(this, {
        path: '/ops/message-send'
      })
    },
    goDashboardDetail(row) {
      const query = {
        from: 'dashboard',
        fromType: this.activeAuditTab,
        fromStatus: '1',
        fromPage: String(this.tabPageMap[this.activeAuditTab] || DEFAULT_PAGE)
      }
      if (this.activeAuditTab === 'merchant-auth') {
        navigateWithFallback(this, { path: `/audits/merchant-auth/${row.id}`, query })
      } else if (this.activeAuditTab === 'artist-realname') {
        navigateWithFallback(this, { path: `/audits/artist-realname/${row.id}`, query })
      } else if (this.activeAuditTab === 'artist-profile') {
        navigateWithFallback(this, { path: `/audits/artist-profile/${row.id}`, query })
      } else if (this.activeAuditTab === 'artist-star-profile') {
        navigateWithFallback(this, { path: `/audits/artist-star-profile/${row.id}`, query })
      } else if (this.activeAuditTab === 'artist-talent-video') {
        navigateWithFallback(this, { path: `/audits/artist-talent-video/${row.id}`, query })
      } else {
        navigateWithFallback(this, { path: `/audits/casting-notice/${row.id}`, query })
      }
    }
  }
}
</script>

<style scoped>
.dashboard-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.overview-panel {
  margin-top: 20px;
  padding: 24px;
  border-radius: 18px;
  background: linear-gradient(180deg, #fbfcfd 0%, #ffffff 100%);
  border: 1px solid #e8edf3;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.04);
}

.overview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.overview-title {
  color: #1f2937;
  font-size: 24px;
  font-weight: 700;
}

.overview-subtitle {
  margin-top: 8px;
  color: #8b95a7;
  font-size: 13px;
  line-height: 1.6;
}

.overview-total {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 999px;
  background: #f4f7fb;
  color: #4b5563;
}

.overview-total__label {
  font-size: 12px;
  color: #94a3b8;
}

.overview-total__value {
  font-size: 20px;
  font-weight: 700;
}

.stats-grid {
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.stats-card {
  position: relative;
  overflow: hidden;
  padding: 18px 18px 16px;
  border-radius: 16px;
  border: 1px solid #edf1f5;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.04);
}

.stats-card::after {
  content: '';
  position: absolute;
  top: -18px;
  right: -18px;
  width: 90px;
  height: 90px;
  border-radius: 50%;
  opacity: 0.75;
}

.stats-card--artist {
  background: linear-gradient(180deg, #f7fbfa 0%, #eef8f5 100%);
}

.stats-card--artist::after {
  background: rgba(45, 212, 191, 0.12);
}

.stats-card--merchant {
  background: linear-gradient(180deg, #f7faff 0%, #eef4ff 100%);
}

.stats-card--merchant::after {
  background: rgba(96, 165, 250, 0.12);
}

.stats-card--notice {
  background: linear-gradient(180deg, #fff9f5 0%, #fff2e8 100%);
}

.stats-card--notice::after {
  background: rgba(251, 146, 60, 0.14);
}

.stats-card--order {
  background: linear-gradient(180deg, #fff8f5 0%, #ffefe8 100%);
}

.stats-card--order::after {
  background: rgba(249, 115, 22, 0.14);
}

.stats-card__label {
  position: relative;
  z-index: 1;
  font-size: 14px;
  color: #64748b;
}

.stats-card__value {
  position: relative;
  z-index: 1;
  margin-top: 12px;
  font-size: 32px;
  line-height: 1;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #111827;
}

.stats-card__progress {
  position: relative;
  z-index: 1;
  margin-top: 14px;
  width: 100%;
  height: 6px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.14);
}

.stats-card__progress-fill {
  height: 100%;
  border-radius: 999px;
}

.stats-card__progress-fill--artist {
  background: #14b8a6;
}

.stats-card__progress-fill--merchant {
  background: #3b82f6;
}

.stats-card__progress-fill--notice {
  background: #f97316;
}

.stats-card__progress-fill--order {
  background: #ea580c;
}

.stats-card__hint {
  position: relative;
  z-index: 1;
  margin-top: 12px;
  font-size: 12px;
  line-height: 1.5;
  color: #94a3b8;
}

.chart-panel {
  margin-top: 20px;
  padding: 22px 20px 20px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #edf2f7;
}

.chart-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.chart-panel__title {
  color: #1f2937;
  font-size: 18px;
  font-weight: 700;
}

.chart-panel__subtitle {
  color: #8a94a6;
  font-size: 12px;
}

.chart-bars {
  margin-top: 24px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
  align-items: end;
}

.chart-bar-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.chart-bar-item__value {
  color: #374151;
  font-size: 14px;
  font-weight: 600;
}

.chart-bar-track {
  width: 100%;
  max-width: 100px;
  height: 230px;
  padding: 14px 12px 12px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  border-radius: 18px;
  background: linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
}

.chart-bar {
  width: 100%;
  min-height: 16%;
  border-radius: 14px 14px 10px 10px;
  box-shadow: 0 10px 18px rgba(15, 23, 42, 0.12);
  transition: height 0.3s ease;
}

.chart-bar--artist {
  background: linear-gradient(180deg, #5eead4 0%, #14b8a6 100%);
}

.chart-bar--merchant {
  background: linear-gradient(180deg, #93c5fd 0%, #3b82f6 100%);
}

.chart-bar--notice {
  background: linear-gradient(180deg, #fdba74 0%, #f97316 100%);
}

.chart-bar--order {
  background: linear-gradient(180deg, #fb923c 0%, #ea580c 100%);
}

.chart-bar-item__label {
  color: #475569;
  font-size: 13px;
}

.quick-send-banner {
  margin-top: 18px;
  padding: 18px 20px;
  border-radius: 12px;
  border: 1px solid #d9ecff;
  background: linear-gradient(135deg, #f4f9ff 0%, #eef6ff 100%);
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.quick-send-banner:hover {
  border-color: #b3d8ff;
  box-shadow: 0 10px 24px rgba(64, 158, 255, 0.08);
  transform: translateY(-1px);
}

.quick-send-banner__icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: #409eff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}

.quick-send-banner__body {
  flex: 1;
  min-width: 0;
}

.quick-send-banner__title {
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.quick-send-banner__desc {
  margin-top: 6px;
  color: #606266;
  font-size: 13px;
  line-height: 1.6;
}

.audit-workbench {
  min-height: 420px;
}

.workbench-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.pagination-wrap {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 1200px) {
  .stats-grid,
  .chart-bars {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .overview-panel {
    padding: 18px;
  }

  .overview-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .stats-grid,
  .chart-bars {
    grid-template-columns: 1fr;
  }

  .chart-bar-track {
    max-width: 100%;
    height: 180px;
  }
}
</style>
